import { useMemo } from "react";
import { useRouterState } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/seo";
import { trendTagsLine } from "@/lib/social-tags";

/** Returns a contextual { label, message } based on the current pathname. */
function buildShareForPath(pathname: string): { label: string; message: string } {
  const url = `${SITE_URL}${pathname}`;

  // Bill checker (root or any /tools/*-bill* landing)
  if (/utility-bill-checker|bill-check|bill-online|duplicate-bill|bill-by-reference|gas-bill|water-bill/.test(pathname)) {
    return {
      label: "Share Bill Checker with Family",
      message: `Apna bijli/gas bill 1-click me check karein — bina form bhare:\n${url}`,
    };
  }

  // City alias /pakistan/lahore or /city/pakistan/lahore (+/karat)
  const cityMatch = pathname.match(/^\/(?:city\/)?(pakistan|india)\/([a-z-]+)/);
  if (cityMatch) {
    const country = cityMatch[1];
    const city = cityMatch[2].replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const cn = country === "pakistan" ? "Pakistan" : "India";
    return {
      label: `Share Today's ${city} Gold Rate on WhatsApp`,
      message: `Ajj ka ${city} (${cn}) ka gold rate live check karein:\n${url}`,
    };
  }

  // Country hub
  if (pathname === "/pakistan") {
    return { label: "Share Pakistan Gold Rate on WhatsApp", message: `Ajj ka Pakistan ka gold rate live:\n${url}` };
  }
  if (pathname === "/india") {
    return { label: "Share India Gold Rate on WhatsApp", message: `Today's live gold rate in India:\n${url}` };
  }

  // Tools / Developer API / generic
  if (pathname.startsWith("/developer/")) {
    return { label: "Share Free Gold Rate API", message: `Free live gold price API — JSON feed for developers:\n${url}` };
  }
  if (pathname.startsWith("/tools/")) {
    return { label: "Share This Free Tool on WhatsApp", message: `Free gold & utility tools — check it out:\n${url}` };
  }

  return {
    label: "Share DrHint on WhatsApp",
    message: `Live gold rates + free utility bill checker — try it:\n${url}`,
  };
}

function buildHref(message: string) {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

function withTrendTags(message: string, pathname: string): string {
  return `${message}\n\n${trendTagsLine(pathname)}`;
}

/** Floating WhatsApp share button — contextual, mounted globally. */
export function WhatsAppShareFab() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { label, message } = useMemo(() => buildShareForPath(pathname), [pathname]);

  // Hide on admin / embed routes
  if (pathname.startsWith("/admin-portal") || pathname.startsWith("/embed/")) return null;

  const tagged = withTrendTags(message, pathname);
  return (
    <a
      href={buildHref(tagged)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="fixed bottom-5 right-5 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-2xl shadow-emerald-500/30 ring-2 ring-white/10 transition-transform hover:scale-110 active:scale-95"
      style={{ willChange: "transform" }}
    >
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.36-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.894 6.994c-.003 5.45-4.437 9.884-9.887 9.884zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
      </svg>
      <span className="sr-only">{label}</span>
    </a>
  );
}

/** Inline pill — drop inside result cards for stronger conversion. */
export function WhatsAppShareInline({
  label,
  message,
  className = "",
}: {
  label?: string;
  message?: string;
  className?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const ctx = useMemo(() => buildShareForPath(pathname), [pathname]);
  const finalLabel = label ?? ctx.label;
  const finalMessage = message ?? ctx.message;
  const tagged = withTrendTags(finalMessage, pathname);
  return (
    <a
      href={buildHref(tagged)}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-bold text-white shadow-md transition-transform hover:scale-[1.02] active:scale-95 ${className}`}
    >
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      </svg>
      {finalLabel}
    </a>
  );
}